/**
 * Quality tiers.
 *
 * A tier decides how many pixels the frame's work runs over. Every knob it owns is here
 * and every one of them is a resolution or a count: the frame's own pixels, the shadow map's, a
 * baked surface's, and how many instances a particle field draws. Nothing else in the renderer
 * carries a number for what a machine can afford, so one tier change moves all of them together.
 *
 * It never changes the art direction: a low tier is the same frame at less resolution, never a
 * different look. Anything that would recolour, restyle or relight the scene
 * belongs in the effect, not here.
 *
 * The tier is picked once at startup and can be forced with `?tier=low` on the URL, which is what
 * the screenshot tool uses to shoot the same frame at every tier.
 */

export type QualityTier = 'low' | 'medium' | 'high';

/** Ascending, so a tier can be compared and a menu can list them in a sensible order. */
export const QUALITY_TIERS: readonly QualityTier[] = ['low', 'medium', 'high'];

export interface TierSettings {
  /**
   * Fraction of the device pixel ratio the frame is rendered at, then scaled up on presentation.
   * The frame is fill-rate bound, so this is the one knob that makes every pixel cheaper at once.
   */
  readonly renderScale: number;
  /**
   * Texels per side of the sun's shadow map. Square, and a power of two: three allocates exactly
   * what it is asked for, and a non-power-of-two depth target is the one thing a driver is allowed
   * to be slow about.
   *
   * `render/environment/skyRig.ts` rescales the PCF kernel against it, so a coarser map is the same
   * softness at less resolution rather than a harder edge. Without that, dropping a tier would
   * change the look, which is the one thing a tier must never do.
   */
  readonly shadowMapSize: number;
  /**
   * Texels per side a baked surface tile is allowed, as a cap rather than a size: a recipe that
   * asks for less keeps what it asked for, and one that asks for more is baked smaller.
   * `render/materials/textureBaker.ts` bakes the same recipe either way, so a tier buys resolution
   * and never a different surface. Powers of two, so the mip chain stays clean.
   *
   * The bake is CPU work paid on first use of a surface, so this is the one knob that also buys
   * back load time: a quarter of the texels is a quarter of the samples.
   */
  readonly textureMapSize: number;
  /**
   * Fraction of the drops, motes and specks a particle field draws at, against the count its own
   * file authors. A field is thousands of instances of one thing, so half of them is the same
   * weather at half the fill, which is what a tier is allowed to do.
   *
   * A countable burst is not a field. Sparks off one impact are a handful, and dropping three of
   * them is a different impact rather than a cheaper one, so nothing counted in tens reads this.
   */
  readonly particleDensity: number;
  /**
   * Anisotropic samples a texture is filtered with. A ground plane runs away to the horizon at a
   * grazing angle, where an isotropic sample is a blurred smear; every extra sample is another
   * fetch along the same footprint, so this is resolution again rather than a different filter.
   *
   * Applied in `render/materials/materialFactory.ts`, and clamped there to what the driver says it
   * can do: a tier is what the machine can afford, and the cap is what it actually has.
   */
  readonly textureAnisotropy: number;
}

/**
 * Draw distance is deliberately not here.
 *
 * The far plane is already bounded by the haze: `three`'s linear `Fog` reaches the fog colour
 * exactly at `fogFarMetres`, so everything past it is the sky whatever a tier says. Pulling the
 * plane in below that would cut the far half off the hills on the skyline, which is a different
 * picture and not a cheaper one. `render/camera.ts` states the distance the world has to reach,
 * once, for every tier alike.
 */
const SETTINGS: Record<QualityTier, TierSettings> = {
  low: {
    renderScale: 0.7,
    shadowMapSize: 1024,
    textureMapSize: 128,
    particleDensity: 0.5,
    textureAnisotropy: 2,
  },
  medium: {
    renderScale: 0.85,
    shadowMapSize: 2048,
    textureMapSize: 256,
    particleDensity: 0.75,
    textureAnisotropy: 4,
  },
  high: {
    renderScale: 1,
    shadowMapSize: 4096,
    textureMapSize: 512,
    particleDensity: 1,
    textureAnisotropy: 8,
  },
};

export function tierSettings(tier: QualityTier): TierSettings {
  return SETTINGS[tier];
}

function isTier(value: string | null): value is QualityTier {
  return value !== null && QUALITY_TIERS.includes(value as QualityTier);
}

/**
 * The tier `?tier=` demands, or null when the URL says nothing.
 *
 * Exported for [tierPreference](./tierPreference.ts), which has to rank it above the player's own
 * knob: the parameter exists so the screenshot tool can shoot the same frame at every tier, and a
 * saved preference quietly overriding it would make those frames lie.
 */
export function forcedTier(): QualityTier | null {
  const forced = new URLSearchParams(window.location.search).get('tier');
  return isTier(forced) ? forced : null;
}

/**
 * What the machine looks like it can afford, unless the URL says otherwise. Core count, pixel
 * ratio and the pointer are the only budget signals a browser will hand over: a phone reports few
 * cores behind a dense display, which is the worst combination for a fill-rate bound pipeline.
 */
export function defaultTier(): QualityTier {
  const forced = forcedTier();
  if (forced !== null) return forced;

  // A touchscreen with no mouse anywhere is a phone or a tablet. Its core count flatters it: the
  // cores are small, the thermal budget is a few watts, and the panel is dense.
  if (!window.matchMedia('(any-pointer: fine)').matches) return 'low';

  const cores = window.navigator.hardwareConcurrency;
  const dense = window.devicePixelRatio > 2;
  if (cores <= 4 || (dense && cores <= 8)) return 'low';
  if (cores <= 8) return 'medium';
  return 'high';
}

/** Post-processing off entirely, for a before shot or for chasing a bug back to the raw render. */
export function postEnabled(): boolean {
  return new URLSearchParams(window.location.search).get('post') !== 'off';
}
